import { c as _c } from "react/compiler-runtime";
import React, { Suspense, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useClickAway } from 'react-use';
import JBImage from 'components/essentials/JBImage';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import IconClose from 'components/icons/iconClose';
import IconSimpleArrowLeft from 'components/icons/iconSimpleArrowLeft';
import useFitGuideSlides from 'data-hooks/useFitGuideSlides';
import useIsBrowser from 'hooks/useIsBrowser';
import { prefersReducedMotion } from 'hooks/useScrollSelectedIntoView';

// Edge copies let a swipe past either end loop round
const getTrackSlides = slides => {
  const trackSlides = slides.map((slide, position) => ({
    ...slide,
    key: `slide-${position}`
  }));
  return {
    trackSlides,
    loopTrackSlides: [{
      ...slides[slides.length - 1],
      key: 'loop-start'
    }, ...trackSlides, {
      ...slides[0],
      key: 'loop-end'
    }]
  };
};

// Invisible: an edge copy shows the same picture as its real slide
const jumpOffEdgeCopy = (track, position, slideCount) => {
  const loopWidth = slideCount * track.clientWidth;
  if (position <= 0) {
    track.scrollLeft += loopWidth;
    return slideCount;
  }
  if (position >= slideCount + 1) {
    track.scrollLeft -= loopWidth;
    return 1;
  }
  return position;
};
const ArrowButton = t0 => {
  const $ = _c(6);
  const {
    direction,
    onClick
  } = t0;
  const t1 = direction === "prev" ? "Previous slide" : "Next slide";
  const t2 = direction === "next" ? "rotate-180" : undefined;
  let t3;
  if ($[0] !== t2) {
    t3 = <IconSimpleArrowLeft stroke="currentColor" width={9} height={15} className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onClick || $[3] !== t1 || $[4] !== t3) {
    t4 = <button type="button" aria-label={t1} onClick={onClick} className="pointer-events-auto flex h-11 w-11 flex-none items-center justify-center rounded-full border border-solid border-gray-light6 bg-white p-0 hover:cursor-pointer hover:border-brand">{t3}</button>;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
ArrowButton.propTypes = {
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
  onClick: PropTypes.func
};

// Same native scroll-snap approach as PDPSectionCarousel, one slide per page
const FitGuideSlideshow = t0 => {
  const $ = _c(68);
  const {
    slides
  } = t0;
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const slideCount = slides.length;
  let t1;
  if ($[0] !== slides) {
    t1 = getTrackSlides(slides);
    $[0] = slides;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    trackSlides: realSlides,
    loopTrackSlides
  } = t1;
  const looping = useIsBrowser() && slideCount > 1;
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const readMoreRef = useRef(null);
  const frameRef = useRef(0);
  const scrollEndRef = useRef(0);
  const positionRef = useRef(0);
  const bodyId = useId();
  const slide = slides[index];
  const offset = looping ? 1 : 0;
  const trackSlides = looping ? loopTrackSlides : realSlides;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(scrollEndRef.current);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== looping) {
    t4 = () => {
      const track = trackRef.current;
      if (!looping || !track) {
        return;
      }
      positionRef.current = 1;
      const syncScroll = () => {
        track.scrollLeft = positionRef.current * track.clientWidth;
      };
      const observer = new ResizeObserver(syncScroll);
      syncScroll();
      observer.observe(track);
      return () => observer.disconnect();
    };
    t5 = [looping];
    $[4] = looping;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = t7 => {
      const {
        restoreFocus
      } = t7;
      setExpanded(false);
      if (restoreFocus) {
        readMoreRef.current?.focus();
      }
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const closeBody = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setExpanded(false);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useClickAway(sectionRef, t7);
  let t8;
  let t9;
  if ($[9] !== expanded) {
    t8 = () => {
      if (!expanded) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          closeBody({
            restoreFocus: sectionRef.current?.contains(document.activeElement)
          });
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t9 = [expanded];
    $[9] = expanded;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] !== looping || $[13] !== slideCount) {
    t10 = () => {
      const track_0 = trackRef.current;
      if (!track_0?.clientWidth) {
        return;
      }
      const position = Math.round(track_0.scrollLeft / track_0.clientWidth);
      positionRef.current = looping ? jumpOffEdgeCopy(track_0, position, slideCount) : position;
    };
    $[12] = looping;
    $[13] = slideCount;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const handleScrollEnd = t10;
  let t11;
  if ($[15] !== handleScrollEnd || $[16] !== offset || $[17] !== slideCount || $[18] !== trackSlides.length) {
    t11 = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        const track_1 = trackRef.current;
        if (track_1?.clientWidth) {
          const position_0 = Math.min(Math.max(Math.round(track_1.scrollLeft / track_1.clientWidth), 0), trackSlides.length - 1);
          setIndex((position_0 - offset + slideCount) % slideCount);
        }
      });
      clearTimeout(scrollEndRef.current);
      scrollEndRef.current = setTimeout(handleScrollEnd, 150);
    };
    $[15] = handleScrollEnd;
    $[16] = offset;
    $[17] = slideCount;
    $[18] = trackSlides.length;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleScroll = t11;
  let t12;
  if ($[20] !== looping || $[21] !== slideCount) {
    t12 = direction => {
      const track_2 = trackRef.current;
      if (!track_2) {
        return;
      }
      const position_1 = looping ? jumpOffEdgeCopy(track_2, positionRef.current, slideCount) + direction : (positionRef.current + direction + slideCount) % slideCount;
      positionRef.current = position_1;
      track_2.scrollTo({
        left: position_1 * track_2.clientWidth,
        behavior: prefersReducedMotion() ? "auto" : "smooth"
      });
    };
    $[20] = looping;
    $[21] = slideCount;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const step = t12;
  let t13;
  if ($[23] !== index || $[24] !== offset || $[25] !== trackSlides) {
    let t14;
    if ($[27] !== index || $[28] !== offset) {
      t14 = (t15, position_2) => {
        const {
          key,
          imageSrc,
          imageAlt
        } = t15;
        return <div key={key} aria-hidden={position_2 !== index + offset} className="h-full w-full flex-none snap-center"><JBImage className="h-full w-full object-cover" src={imageSrc} alt={imageAlt} sizes="(max-width: 767px) 100vw, 30vw" /></div>;
      };
      $[27] = index;
      $[28] = offset;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    t13 = trackSlides.map(t14);
    $[23] = index;
    $[24] = offset;
    $[25] = trackSlides;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[30] !== handleScroll || $[31] !== t13) {
    t14 = <div ref={trackRef} onScroll={handleScroll} className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{t13}</div>;
    $[30] = handleScroll;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== step) {
    t15 = <ArrowButton direction="prev" onClick={() => step(-1)} />;
    $[33] = step;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== step) {
    t16 = <ArrowButton direction="next" onClick={() => step(1)} />;
    $[35] = step;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">{t15}{t16}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t17) {
    t18 = <div className="relative aspect-video">{t14}{t17}</div>;
    $[40] = t14;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = `${index + 1} of ${slideCount}`;
  let t20;
  if ($[43] !== slide.heading) {
    t20 = <h4 aria-live="polite" className="m-0">{slide.heading}</h4>;
    $[43] = slide.heading;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setExpanded(_temp);
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = expanded ? "Read Less" : "Read More";
  let t23;
  if ($[46] !== bodyId || $[47] !== expanded || $[48] !== t22) {
    t23 = <button ref={readMoreRef} type="button" aria-expanded={expanded} aria-controls={bodyId} onClick={t21} className="inline-flex h-11 flex-none items-center border-0 bg-transparent p-0 text-xs font-bold text-brand no-underline hover:cursor-pointer hover:underline">{t22}</button>;
    $[46] = bodyId;
    $[47] = expanded;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t20 || $[51] !== t23) {
    t24 = <div className="flex items-center justify-between gap-4">{t20}{t23}</div>;
    $[50] = t20;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  const t25 = !expanded;
  let t26;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => closeBody({
      restoreFocus: true
    });
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <button type="button" aria-label="Close" onClick={t26} className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 hover:cursor-pointer"><IconClose width={9} height={9} /></button>;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== slide.bodyHtml) {
    t28 = <SanitizedHtml html={slide.bodyHtml} />;
    $[55] = slide.bodyHtml;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== bodyId || $[58] !== t25 || $[59] !== t28) {
    t29 = <div id={bodyId} hidden={t25} className="absolute inset-x-0 top-full z-10 border border-solid border-gray-light6 bg-white py-4 pl-4 pr-11 shadow-md">{t27}{t28}</div>;
    $[57] = bodyId;
    $[58] = t25;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t19 || $[62] !== t24 || $[63] !== t29) {
    t30 = <div aria-roledescription="slide" aria-label={t19} role="group" className="relative">{t24}{t29}</div>;
    $[61] = t19;
    $[62] = t24;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t18 || $[66] !== t30) {
    t31 = <section ref={sectionRef} aria-roledescription="carousel" aria-label="Measuring for Delivery" data-xc="PDPMeasureForDeliverySlideshow">{t18}{t30}</section>;
    $[65] = t18;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  return t31;
};
FitGuideSlideshow.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.string.isRequired,
    bodyHtml: PropTypes.string.isRequired,
    imageSrc: PropTypes.string.isRequired,
    imageAlt: PropTypes.string
  })).isRequired
};
const FitGuideSlideshowFromCMS = () => {
  const $ = _c(2);
  const slides = useFitGuideSlides();
  let t0;
  if ($[0] !== slides) {
    t0 = slides.length > 0 ? <FitGuideSlideshow key={slides.length} slides={slides} /> : null;
    $[0] = slides;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

// Own boundary: the next one up wraps every info tab
const PDPMeasureForDeliverySlideshow = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><FitGuideSlideshowFromCMS /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default PDPMeasureForDeliverySlideshow;
function _temp(open) {
  return !open;
}